import { NgClass } from '@angular/common'
import type { Signal } from '@angular/core'
import { Component, inject, input } from '@angular/core'
import { Router, RouterLink, RouterLinkActive, isActive } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'

import type { DialogState } from '../../../../services/dialog/dialog.service'
import { DialogService } from '../../../../services/dialog/dialog.service'
import { ClickOutsideDirective } from '../../../../shared/directives/click-outside.directive'
import type { NavItem } from '../../interfaces/nav-item.interface'

@Component({
  selector: 'dhp-desktop-nav',
  templateUrl: './desktop-nav.component.html',
  imports: [
    NgClass,
    ClickOutsideDirective,
    RouterLink,
    RouterLinkActive,
    TranslatePipe,
  ],
})
export class DesktopNavComponent {
  dialogService = inject(DialogService)
  private router = inject(Router)
  private readonly routeActiveSignals = new Map<string, Signal<boolean>>()

  readonly navItems = input<NavItem[]>([])

  toggleDropdown(dropdownKey: keyof DialogState, event?: MouseEvent): void {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    this.dialogService.toggleDropdown(dropdownKey)
  }

  handleDropdownItemClick(item: NavItem): void {
    if (item.dropdownItems) {
      this.dialogService.toggle(item.dropdown!, false)
    }
  }

  isDropdownOpen(dropdownKey: keyof DialogState): boolean {
    return this.dialogService.isOpen(dropdownKey)
  }

  hasActiveChild(item: NavItem): boolean {
    if (!item.dropdownItems) return false
    return item.dropdownItems.some(dropdownItem =>
      this.isRouteActive(dropdownItem.url)
    )
  }

  isRouteActive(url: string | undefined): boolean {
    if (!url) return false
    const cleanUrl = url.startsWith('/') ? url : `/${url}`
    return this.getRouteActiveSignal(cleanUrl)()
  }

  private getRouteActiveSignal(url: string): Signal<boolean> {
    let activeSignal = this.routeActiveSignals.get(url)
    if (!activeSignal) {
      activeSignal = isActive(url, this.router, {
        paths: 'exact',
        queryParams: 'exact',
        fragment: 'ignored',
        matrixParams: 'ignored',
      })
      this.routeActiveSignals.set(url, activeSignal)
    }

    return activeSignal
  }

  closeDropdown(dropdownKey: keyof DialogState): void {
    this.dialogService.toggle(dropdownKey, false)
  }
}
